/** Seed data for the Procurement Readiness guided workspace (Demo Mode only). Represents the
 * Stage 5 onboarding/mobilisation moment: preparing a customer site to order, receive and use
 * ingredients, products and consumables for daily service, hospitality and events. No backend
 * "procurement readiness" feed exists yet - mock only, mirrors the seeded-data approach used by
 * src/mocks/data/procurementOverviewData.ts. Site codes/names/accounts reuse the same set as the
 * Procurement Overview so the two screens stay consistent. */

export type ReadinessStatus =
  | 'Not started'
  | 'Requirements received'
  | 'Supplier setup in progress'
  | 'Catalogue setup in progress'
  | 'Product mapping in progress'
  | 'Awaiting pricing'
  | 'Awaiting delivery schedule'
  | 'Awaiting order channel setup'
  | 'Exceptions to resolve'
  | 'Opening stock pending'
  | 'Ready for approval'
  | 'Approved'
  | 'Ready for go-live'
  | 'Live';

export const READINESS_STATUS_COLOR: Record<ReadinessStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  'Not started': 'default',
  'Requirements received': 'info',
  'Supplier setup in progress': 'info',
  'Catalogue setup in progress': 'info',
  'Product mapping in progress': 'info',
  'Awaiting pricing': 'warning',
  'Awaiting delivery schedule': 'warning',
  'Awaiting order channel setup': 'warning',
  'Exceptions to resolve': 'error',
  'Opening stock pending': 'warning',
  'Ready for approval': 'info',
  Approved: 'success',
  'Ready for go-live': 'success',
  Live: 'success',
};

export interface SiteProcurementReadiness {
  siteCode: string;
  siteName: string;
  clientAccount: string;
  siteGroup: string;
  approvedRangeReceived: boolean;
  status: ReadinessStatus;
  supplierReadinessPct: number;
  catalogueReadinessPct: number;
  productReadinessPct: number;
  deliveryReadinessPct: number;
  openingStockReadinessPct: number;
  openExceptions: number;
  blockers: string[];
  nextBestAction: string;
  owner: string;
  lastUpdated: string;
}

export const siteProcurementReadiness: SiteProcurementReadiness[] = [
  {
    siteCode: '0101', siteName: 'High Street Central', clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - North',
    approvedRangeReceived: true, status: 'Ready for go-live',
    supplierReadinessPct: 100, catalogueReadinessPct: 100, productReadinessPct: 100, deliveryReadinessPct: 100, openingStockReadinessPct: 100,
    openExceptions: 0, blockers: [], nextBestAction: 'Confirm go-live date with site manager.', owner: 'J. Okafor', lastUpdated: '2026-07-08',
  },
  {
    siteCode: '0102', siteName: 'Riverside School', clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - North',
    approvedRangeReceived: true, status: 'Exceptions to resolve',
    supplierReadinessPct: 90, catalogueReadinessPct: 80, productReadinessPct: 70, deliveryReadinessPct: 60, openingStockReadinessPct: 0,
    openExceptions: 2, blockers: ['Delivery schedule missing for Northern Dairy Co-op', 'Opening stock not configured'],
    nextBestAction: 'Confirm delivery schedule with Northern Dairy Co-op.', owner: 'J. Okafor', lastUpdated: '2026-07-07',
  },
  {
    siteCode: '0145', siteName: 'Fairview Academy', clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - South',
    approvedRangeReceived: true, status: 'Awaiting order channel setup',
    supplierReadinessPct: 100, catalogueReadinessPct: 100, productReadinessPct: 90, deliveryReadinessPct: 40, openingStockReadinessPct: 0,
    openExceptions: 1, blockers: ['Order channel not configured for Harvest Grain Merchants'],
    nextBestAction: 'Configure order channel for Harvest Grain Merchants.', owner: 'M. Patel', lastUpdated: '2026-07-05',
  },
  {
    siteCode: '0201', siteName: 'Northgate General', clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Acute',
    approvedRangeReceived: false, status: 'Not started',
    supplierReadinessPct: 0, catalogueReadinessPct: 0, productReadinessPct: 0, deliveryReadinessPct: 0, openingStockReadinessPct: 0,
    openExceptions: 1, blockers: ['Approved range not yet received from Ranges'],
    nextBestAction: 'Follow up with Ranges for approved range sign-off.', owner: 'M. Patel', lastUpdated: '2026-06-30',
  },
  {
    siteCode: '0202', siteName: 'Downtown Community Clinic', clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Community',
    approvedRangeReceived: true, status: 'Awaiting pricing',
    supplierReadinessPct: 80, catalogueReadinessPct: 60, productReadinessPct: 50, deliveryReadinessPct: 30, openingStockReadinessPct: 0,
    openExceptions: 2, blockers: ['Price approval missing for Prime Cuts Butchery', 'Product unavailable - Mixed Spice Blend 1kg'],
    nextBestAction: 'Chase commercial price approval for Prime Cuts Butchery.', owner: 'S. Wren', lastUpdated: '2026-07-06',
  },
  {
    siteCode: '0301', siteName: 'Uptown Offices', clientAccount: 'Ridgeway Council', siteGroup: 'Corporate Dining',
    approvedRangeReceived: true, status: 'Ready for approval',
    supplierReadinessPct: 100, catalogueReadinessPct: 100, productReadinessPct: 100, deliveryReadinessPct: 100, openingStockReadinessPct: 90,
    openExceptions: 0, blockers: [], nextBestAction: 'Complete final approval checklist and sign off.', owner: 'S. Wren', lastUpdated: '2026-07-08',
  },
];

/** Final approval checklist - see Stage 5 spec "Procurement readiness checklist". Items marked
 * `critical: true` block "Ready for go-live" while incomplete. */
export interface ChecklistItem {
  id: string;
  label: string;
  critical: boolean;
}

export const READINESS_CHECKLIST: ChecklistItem[] = [
  { id: 'approvedRange', label: 'Approved range received', critical: true },
  { id: 'ingredientsIdentified', label: 'Required ingredients identified', critical: true },
  { id: 'consumablesIdentified', label: 'Required consumables identified', critical: false },
  { id: 'suppliersAssigned', label: 'Suppliers assigned', critical: true },
  { id: 'supplierOnboarding', label: 'Supplier onboarding complete', critical: true },
  { id: 'orderChannels', label: 'Order channels configured', critical: true },
  { id: 'cataloguesAssigned', label: 'Catalogues assigned', critical: true },
  { id: 'productsPresent', label: 'Required products present', critical: true },
  { id: 'productMappings', label: 'Product mappings complete', critical: false },
  { id: 'pricesApproved', label: 'Prices approved', critical: true },
  { id: 'packSizesValidated', label: 'Pack sizes validated', critical: false },
  { id: 'uomValidated', label: 'Units of measure validated', critical: false },
  { id: 'deliverySchedules', label: 'Delivery schedules confirmed', critical: true },
  { id: 'substitutions', label: 'Substitutions approved', critical: false },
  { id: 'openingStock', label: 'Opening stock configured', critical: true },
  { id: 'standingOrders', label: 'Standing orders / auto shipments configured where required', critical: false },
  { id: 'criticalExceptions', label: 'Critical exceptions resolved', critical: true },
  { id: 'siteApproval', label: 'Site procurement approval complete', critical: true },
];

/** Per-site checklist completion. Keyed by ChecklistItem id. Not present = incomplete. */
export const siteChecklistProgress: Record<string, Record<string, boolean>> = {
  '0101': Object.fromEntries(READINESS_CHECKLIST.map((item) => [item.id, true])),
  '0102': {
    approvedRange: true, ingredientsIdentified: true, suppliersAssigned: true, supplierOnboarding: true,
    orderChannels: true, cataloguesAssigned: true, productsPresent: true, pricesApproved: true,
  },
  '0145': {
    approvedRange: true, ingredientsIdentified: true, consumablesIdentified: true, suppliersAssigned: true,
    supplierOnboarding: true, cataloguesAssigned: true, productsPresent: true, productMappings: true, pricesApproved: true,
  },
  '0201': {},
  '0202': {
    approvedRange: true, ingredientsIdentified: true, suppliersAssigned: true, supplierOnboarding: true,
    orderChannels: true, cataloguesAssigned: true,
  },
  '0301': Object.fromEntries(READINESS_CHECKLIST.filter((item) => item.id !== 'siteApproval').map((item) => [item.id, true])),
};

/** Requirements tab - static inputs carried across from Customer Onboarding, Estate and Ranges. */
export interface SiteRequirements {
  servicePeriods: string;
  expectedCovers: string;
  menus: string;
  recipes: string;
  ingredients: string;
  consumables: string;
  hospitalityRequirements: string;
  eventsRequirements: string;
  storageConstraints: string;
  deliveryConstraints: string;
  sustainabilityRequirements: string;
}

export const siteRequirements: Record<string, SiteRequirements> = {
  '0101': {
    servicePeriods: 'Breakfast, lunch, mid-morning break',
    expectedCovers: '450 covers/day',
    menus: 'Primary School Menu v3',
    recipes: '82 approved recipes',
    ingredients: '210 approved ingredients',
    consumables: 'Napkins, compostable trays, gloves',
    hospitalityRequirements: 'Termly parents evening catering (x3/year)',
    eventsRequirements: 'Sports day BBQ (annual)',
    storageConstraints: 'Ambient store 12m\u00b2, 1 walk-in chiller',
    deliveryConstraints: 'AM deliveries only, 07:00-09:00',
    sustainabilityRequirements: 'Local sourcing target 30% by spend',
  },
  '0102': {
    servicePeriods: 'Breakfast, lunch',
    expectedCovers: '320 covers/day',
    menus: 'Primary School Menu v3',
    recipes: '76 approved recipes',
    ingredients: '198 approved ingredients',
    consumables: 'Napkins, compostable trays',
    hospitalityRequirements: 'None confirmed',
    eventsRequirements: 'None confirmed',
    storageConstraints: 'Ambient store 8m\u00b2, shared chiller',
    deliveryConstraints: 'AM deliveries only, 07:30-09:00, narrow access lane',
    sustainabilityRequirements: 'Local sourcing target 30% by spend',
  },
  '0145': {
    servicePeriods: 'Breakfast, lunch, after-school club',
    expectedCovers: '540 covers/day',
    menus: 'Secondary School Menu v2',
    recipes: '104 approved recipes',
    ingredients: '245 approved ingredients',
    consumables: 'Napkins, trays, allergen labels',
    hospitalityRequirements: 'Open evenings catering (x2/year)',
    eventsRequirements: 'Prom catering (annual)',
    storageConstraints: 'Ambient store 18m\u00b2, 2 walk-in chillers, 1 freezer',
    deliveryConstraints: 'AM and midday deliveries permitted',
    sustainabilityRequirements: 'Local sourcing target 25% by spend',
  },
  '0201': {
    servicePeriods: 'Breakfast, lunch, dinner, 24hr ward service',
    expectedCovers: '900 covers/day',
    menus: 'Acute Ward Menu v5',
    recipes: 'Pending Ranges sign-off',
    ingredients: 'Pending Ranges sign-off',
    consumables: 'Clinical-grade trays, allergen labels',
    hospitalityRequirements: 'Staff canteen catering',
    eventsRequirements: 'None confirmed',
    storageConstraints: 'Pending Estate confirmation',
    deliveryConstraints: 'Pending Estate confirmation',
    sustainabilityRequirements: 'Local sourcing target 20% by spend',
  },
  '0202': {
    servicePeriods: 'Lunch only',
    expectedCovers: '140 covers/day',
    menus: 'Community Clinic Menu v1',
    recipes: '38 approved recipes',
    ingredients: '96 approved ingredients',
    consumables: 'Napkins, trays',
    hospitalityRequirements: 'None confirmed',
    eventsRequirements: 'None confirmed',
    storageConstraints: 'Ambient store 5m\u00b2, shared chiller',
    deliveryConstraints: 'AM deliveries only, restricted loading bay',
    sustainabilityRequirements: 'Local sourcing target 20% by spend',
  },
  '0301': {
    servicePeriods: 'Breakfast, lunch, hospitality',
    expectedCovers: '260 covers/day',
    menus: 'Corporate Dining Menu v4',
    recipes: '64 approved recipes',
    ingredients: '150 approved ingredients',
    consumables: 'Premium napkins, crockery hire',
    hospitalityRequirements: 'Boardroom hospitality (daily)',
    eventsRequirements: 'Quarterly client events',
    storageConstraints: 'Ambient store 10m\u00b2, 1 walk-in chiller',
    deliveryConstraints: 'AM and midday deliveries permitted',
    sustainabilityRequirements: 'Local sourcing target 35% by spend',
  },
};
